import { setInteractionError } from '@atlassian/react-ufo/set-interaction-error';

import { thunkProgress } from 'redux-progress';

import selectCartPayload from 'modules/cart/duck/selectors/cart';
import { fetchOrCreateCart } from 'modules/cart/duck/actions';
import { monitorStatus } from 'modules/checkout/review/duck/actions/monitorStatus';
import submitCreditCard from 'modules/checkout/review/duck/actions/submitCreditCard';
import submitFreeOrder from 'modules/checkout/review/duck/actions/submitFreeOrder';
import convertToQuote from 'modules/checkout/review/duck/actions/convertToQuote';
import submitOnAccount from 'modules/checkout/review/duck/actions/submitOnAccount';
import submitOnTerms from 'modules/checkout/review/duck/actions/submitOnTerms';
import submitWithPaypal from 'modules/checkout/review/duck/actions/submitWithPaypal';
import { selectPurchaseMode } from 'modules/settings/purchaseMode';
import * as payment from 'modules/checkout/payment';
import { CartThunk, Dispatch, GetState, State } from 'model/State';
import { api } from 'modules/checkout/payment';
import {
  CART_SUBMIT_STRIPE_UFO_NAME,
  CART_SUBMIT_CONVERT_TO_QUOTE_UFO_NAME,
  CART_SUBMIT_PAY_ON_TERMS_UFO_NAME,
  CART_SUBMIT_PAY_ON_ACCOUNT_UFO_NAME,
  CART_SUBMIT_PAYPAL_UFO_NAME,
  CART_SUBMIT_FREE_UFO_NAME,
  UNKNOWN_PAYMENT_METHOD_UFO_NAME,
} from 'util/ufoNameConstants';

import * as CheckoutTypes from './CheckoutTypes';

import { mightRequirePaymentMethodToBeRefilled, TNS_ERROR_MESSAGE } from './error-keys';

export const CHECKOUT = 'checkout/review/CHECKOUT';

export const PLACE_ORDER = 'checkout/review/PLACE_ORDER';

export const HamsOrderStatusCode = {
  CREDIT_CARD_DECLINED: 'CREDIT_CARD_DECLINED',
};

const checkoutModesToCheckoutTypes: {
  [S in keyof typeof payment.model.CheckoutModes]: CheckoutTypes.CheckoutType;
} = {
  [payment.model.CheckoutModes.STRIPE_CREDIT_CARD]: CheckoutTypes.STRIPE,
  [payment.model.CheckoutModes.PAY_ON_ACCOUNT]: CheckoutTypes.PAY_ON_ACCOUNT,
  [payment.model.CheckoutModes.PAY_ON_TERMS]: CheckoutTypes.PAY_ON_TERMS,
  [payment.model.CheckoutModes.PAYPAL]: CheckoutTypes.PAYPAL,
  [payment.model.CheckoutModes.BANK_TRANSFER]: CheckoutTypes.CART_CONVERT_TO_QUOTE,
  [payment.model.CheckoutModes.FREE]: CheckoutTypes.FREE,
};

type SubmitFunction = (
  cartId: string,
  checkoutType: CheckoutTypes.CheckoutType,
) => (dispatch: Dispatch, getState: GetState) => Promise<any>;

const checkoutTypeToHandler: {
  [S in CheckoutTypes.CheckoutType]: SubmitFunction;
} = {
  [CheckoutTypes.FREE]: submitFreeOrder,
  [CheckoutTypes.STRIPE]: submitCreditCard,
  [CheckoutTypes.CART_CONVERT_TO_QUOTE]: convertToQuote,
  [CheckoutTypes.PAY_ON_ACCOUNT]: submitOnAccount,
  [CheckoutTypes.PAY_ON_TERMS]: submitOnTerms,
  [CheckoutTypes.PAYPAL]: submitWithPaypal as SubmitFunction,
};

const getCheckoutOperation = (state: State): CheckoutTypes.CheckoutType => {
  const cart = selectCartPayload(state).unwrap();
  const purchaseMode = selectPurchaseMode(state);
  const { checkoutMode } = state.checkout.payment;

  if (cart.totalIncTax === 0) {
    return CheckoutTypes.FREE;
  }

  if (cart.inEditMode || purchaseMode === 'QUOTE') {
    return CheckoutTypes.CART_CONVERT_TO_QUOTE;
  }

  if (!checkoutModesToCheckoutTypes[checkoutMode]) {
    throw new Error(`Unknown checkout mode ${checkoutMode}`);
  }

  return checkoutModesToCheckoutTypes[checkoutMode];
};

const selectUFOEventForCheckoutType = (
  checkoutType: CheckoutTypes.CheckoutType,
  processResult: { error: { errorDetail: string }; failed?: boolean; unwrap(): void },
): string => {
  let UFOEvent = '';
  switch (checkoutType) {
    case 'stripe':
      if (processResult.error.errorDetail !== 'Your card was declined.') {
        UFOEvent = CART_SUBMIT_STRIPE_UFO_NAME;
      }
      break;
    case 'cart.convert.to.quote':
      UFOEvent = CART_SUBMIT_CONVERT_TO_QUOTE_UFO_NAME;
      break;
    case 'pay.on.terms':
      UFOEvent = CART_SUBMIT_PAY_ON_TERMS_UFO_NAME;
      break;
    case 'pay.on.account':
      UFOEvent = CART_SUBMIT_PAY_ON_ACCOUNT_UFO_NAME;
      break;
    case 'paypal':
      UFOEvent = CART_SUBMIT_PAYPAL_UFO_NAME;
      break;
    case 'free':
      UFOEvent = CART_SUBMIT_FREE_UFO_NAME;
      break;
    default:
      UFOEvent = UNKNOWN_PAYMENT_METHOD_UFO_NAME;
      break;
  }
  return UFOEvent;
};

const enqueueOrder = (): CartThunk => async (dispatch, getState) => {
  const checkoutType = getCheckoutOperation(getState());
  const cart = selectCartPayload(getState()).unwrap();

  const processResult = await dispatch(
    checkoutTypeToHandler[checkoutType](cart.uuid, checkoutType) as any,
  );

  if (processResult) {
    if (processResult.failed) {
      const UFOEvent = selectUFOEventForCheckoutType(checkoutType, processResult);

      setInteractionError(UFOEvent, {
        errorMessage: `${UFOEvent} error`,
        name: `${UFOEvent} error`,
      });

      if (mightRequirePaymentMethodToBeRefilled(processResult.error?.errorKey)) {
        // we will clear the payment details from storage as best effort if we get the tns error message
        if (processResult.error.errorDetail === TNS_ERROR_MESSAGE) {
          api.storage.paymentDetails.reset();
        }

        await dispatch(fetchOrCreateCart(cart.uuid));
        dispatch(
          payment.actions.route({
            cardError: true,
            ccErrorMessage: processResult.error.errorDetail,
          }),
        );
      }

      return processResult;
    }

    processResult.unwrap();
  }

  return dispatch(monitorStatus(cart.uuid));
};

export const submit = () => (dispatch, getState) =>
  dispatch(
    thunkProgress(CHECKOUT, dispatch(enqueueOrder()), {
      checkoutType: getCheckoutOperation(getState()),
    }),
  );
